// Facebook sign-in via the Cognito Hosted UI's OAuth 2.0 authorization code
// + PKCE flow -- the one path in this app that isn't amazon-cognito-identity-js's
// direct SRP sign-in (see src/lib/auth.ts's header comment). Kept in its own
// module so email/password sign-in is untouched if this is ever removed.
//
// After the Hosted UI redirects back with a code, completeFacebookCallback()
// exchanges it for tokens and bridges them into amazon-cognito-identity-js's
// own session cache via CognitoUser.setSignInUserSession() -- the same
// mechanism the SDK uses internally after a native sign-in -- so
// AuthContext's existing getCurrentSession()-based state (and token refresh)
// treats a Facebook session exactly like a native one with no changes.
import {
  CognitoAccessToken,
  CognitoIdToken,
  CognitoRefreshToken,
  CognitoUser,
  CognitoUserPool,
  CognitoUserSession,
} from "amazon-cognito-identity-js";
import { challengeFromVerifier, randomState, randomVerifier } from "./pkce";

const COGNITO_DOMAIN = import.meta.env.VITE_COGNITO_DOMAIN;
const USER_POOL_ID = import.meta.env.VITE_USER_POOL_ID;
const CLIENT_ID = import.meta.env.VITE_USER_POOL_CLIENT_ID;
// Derived from the page's own origin, not baked in at build time: the same
// build is served on several hostnames (rcccs-demo.sturgeon.tech,
// www.rcracehq.com, ...) and the PKCE verifier lives in sessionStorage, which
// is per-origin -- so the Hosted UI has to send the browser back to the host
// it started on. Every such origin must be in Cognito's CallbackURLs
// (deploy.mjs sets our own hosts; the backend's domain poller adds each
// provisioned club domain, see backend/src/lib/clubOAuthUrls.ts).
const REDIRECT_URI = `${window.location.origin}/auth/callback`;

const SS_VERIFIER = "rcracehq_fb_pkce_verifier";
const SS_STATE = "rcracehq_fb_oauth_state";

let pool: CognitoUserPool | null = null;
function getPool(): CognitoUserPool {
  if (!pool) pool = new CognitoUserPool({ UserPoolId: USER_POOL_ID, ClientId: CLIENT_ID });
  return pool;
}

/** Redirects the browser to the Hosted UI, pinned to the Facebook IdP. */
export async function startFacebookAuthorize(): Promise<void> {
  if (!COGNITO_DOMAIN) {
    throw new Error("Facebook sign-in is not configured for this deployment.");
  }
  const verifier = randomVerifier();
  const challenge = await challengeFromVerifier(verifier);
  const state = randomState();
  sessionStorage.setItem(SS_VERIFIER, verifier);
  sessionStorage.setItem(SS_STATE, state);

  const params = new URLSearchParams({
    response_type: "code",
    client_id: CLIENT_ID,
    redirect_uri: REDIRECT_URI,
    scope: "openid email profile",
    code_challenge_method: "S256",
    code_challenge: challenge,
    state,
    identity_provider: "Facebook",
  });
  window.location.assign(`${COGNITO_DOMAIN}/oauth2/authorize?${params.toString()}`);
}

/** Call from the /auth/callback route. Returns the bridged session on success. */
export async function completeFacebookCallback(): Promise<CognitoUserSession> {
  const url = new URL(window.location.href);
  const code = url.searchParams.get("code");
  const state = url.searchParams.get("state");
  const errParam = url.searchParams.get("error_description") ?? url.searchParams.get("error");
  if (errParam) throw new Error(errParam);
  if (!code) throw new Error("Missing authorization code");

  const expectedState = sessionStorage.getItem(SS_STATE);
  if (!state || state !== expectedState) throw new Error("State mismatch");
  const verifier = sessionStorage.getItem(SS_VERIFIER);
  if (!verifier) throw new Error("Missing PKCE verifier");

  const body = new URLSearchParams({
    grant_type: "authorization_code",
    client_id: CLIENT_ID,
    code,
    redirect_uri: REDIRECT_URI,
    code_verifier: verifier,
  });

  const res = await fetch(`${COGNITO_DOMAIN}/oauth2/token`, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: body.toString(),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Token exchange failed: ${text}`);
  }
  const json = (await res.json()) as {
    id_token: string;
    access_token: string;
    refresh_token?: string;
  };

  sessionStorage.removeItem(SS_VERIFIER);
  sessionStorage.removeItem(SS_STATE);

  const idToken = new CognitoIdToken({ IdToken: json.id_token });
  const claims = idToken.decodePayload() as { sub: string; "cognito:username"?: string };
  const username = claims["cognito:username"] ?? claims.sub;

  const session = new CognitoUserSession({
    IdToken: idToken,
    AccessToken: new CognitoAccessToken({ AccessToken: json.access_token }),
    RefreshToken: json.refresh_token ? new CognitoRefreshToken({ RefreshToken: json.refresh_token }) : undefined,
  });

  const cognitoUser = new CognitoUser({ Username: username, Pool: getPool() });
  cognitoUser.setSignInUserSession(session);

  return session;
}
